import { createContext, useContext, useMemo, useState } from 'react';
import type { FilterCriteria } from '../FiltersBar/types.ts';
import type { FiltersContext, FiltersContextProviderProps, FiltersErrors } from './types.ts';

export const filtersContext = createContext<FiltersContext<FilterCriteria>>({
  filtersConfig: [],
  visibleFilters: [],
  criteria: {},
  hideClearFiltersButton: false,
  defaultFilters: undefined,
  onFiltersChange: () => {
    throw new Error('onCriteriaChange: Not implemented. Ensure you are inside the FiltersContext');
  },
  disabledFilters: [],
  setDisabledFilters: () => {
    throw new Error('setDisableFilters: Not implemented. Ensure you are inside the FiltersContext');
  },
  filtersErrors: {},
  setFiltersErrors: () => {
    throw new Error('setFiltersErrors: Not implemented. Ensure you are inside the FiltersContext');
  },
  isFilteringDisabled: false,
  setIsFilteringDisabled: () => {
    throw new Error('setIsFilteringDisabled: Not implemented. Ensure you are inside the FiltersContext');
  },
  loadingFilters: [],
});

export const useFiltersContext = <Criteria extends FilterCriteria>() => {
  const context = useContext(filtersContext);

  return context as unknown as FiltersContext<Criteria>;
};

export const FiltersContextProvider = <Criteria extends FilterCriteria>({
  config,
  children,
}: FiltersContextProviderProps<Criteria>) => {
  const [disabledFilters, setDisabledFilters] = useState<Extract<Exclude<keyof Criteria, 'phrase'>, string>[]>(
    config.disabledFilters ?? []
  );
  const [filtersErrors, setFiltersErrors] = useState<FiltersErrors>({});
  const [isFilteringDisabled, setIsFilteringDisabled] = useState(false);

  const value = useMemo<FiltersContext<Criteria>>(
    () => ({
      ...config,
      disabledFilters,
      setDisabledFilters,
      filtersErrors,
      setFiltersErrors,
      isFilteringDisabled,
      setIsFilteringDisabled,
    }),
    [config, disabledFilters, filtersErrors, isFilteringDisabled]
  );

  return (
    <filtersContext.Provider value={value as unknown as FiltersContext<FilterCriteria>}>
      {children}
    </filtersContext.Provider>
  );
};
